// ./src/components/features/Artists/ArtistsFilterBar/ArtistsFilterBar.tsx
//
// Filter controls for the Artists library: by kind (band / DJ / solo…) and by
// genre/category. Fully controlled — the page owns the value and maps it to an
// ArtistFilter. Mirrors InventoryItemsFilterBar.

import React from "react";
import { Group, Box, Stack, Button } from "@mantine/core";
import { useTranslation } from "react-i18next";

import { ARTIST_KIND_VALUES, ARTIST_KIND_LABELS } from "@/utils/api";
import { AppIcon, CategorySelect, SelectField } from "@/components/generic";

export interface ArtistsFilterValue {
  kind: string | null;
  categoryId: string | null;
}

export interface ArtistsFilterBarProps {
  value: ArtistsFilterValue;
  onChange: (value: ArtistsFilterValue) => void;
  disabled?: boolean;
  showClear?: boolean;
  vertical?: boolean;
}

export const EMPTY_ARTISTS_FILTER: ArtistsFilterValue = {
  kind: null,
  categoryId: null,
};

const isFilterActive = (value: ArtistsFilterValue): boolean => Boolean(value.kind) || Boolean(value.categoryId);

const CONTROL_STYLE: React.CSSProperties = { flex: "1 1 200px", minWidth: 180 };

const ArtistsFilterBarComponent: React.FC<ArtistsFilterBarProps> = ({
  value,
  onChange,
  disabled = false,
  showClear = true,
  vertical = false,
}) => {
  const { t } = useTranslation(["artists", "common"]);

  const active = isFilterActive(value);

  const kindOptions = ARTIST_KIND_VALUES.map((k) => ({
    value: k,
    label: ARTIST_KIND_LABELS[k] ?? k,
  }));

  const kindControl = (
    <Box style={vertical ? undefined : CONTROL_STYLE}>
      <SelectField
        label={t("artists:filters.kind.label", { defaultValue: "Kind" })}
        placeholder={t("artists:filters.kind.placeholder", { defaultValue: "All kinds" })}
        data={kindOptions}
        value={value.kind}
        onChange={(_name, v) => onChange({ ...value, kind: v })}
        clearable
        disabled={disabled}
        size="md"
      />
    </Box>
  );

  const categoryControl = (
    <Box style={vertical ? undefined : CONTROL_STYLE}>
      <CategorySelect
        group="artist"
        exactGroup
        label={t("artists:filters.category.label", { defaultValue: "Genre" })}
        placeholder={t("artists:filters.category.placeholder", { defaultValue: "All genres" })}
        value={value.categoryId}
        onChange={(id) => onChange({ ...value, categoryId: id })}
        clearable
        disabled={disabled}
        size="md"
      />
    </Box>
  );

  if (vertical) {
    return (
      <Stack gap="md">
        {kindControl}
        {categoryControl}
      </Stack>
    );
  }

  return (
    <Group gap="sm" align="flex-end" wrap="wrap">
      {kindControl}
      {categoryControl}

      {active && showClear && (
        <Button
          variant="subtle"
          color="gray"
          size="md"
          onClick={() => onChange(EMPTY_ARTISTS_FILTER)}
          disabled={disabled}
          leftSection={<AppIcon iconName="X" size={16} />}
        >
          {t("common:buttons.clear", { defaultValue: "Clear" })}
        </Button>
      )}
    </Group>
  );
};

export const ArtistsFilterBar = React.memo(ArtistsFilterBarComponent);
